import React, { useEffect, useState } from 'react';
import { ImageOff } from 'lucide-react';

export interface SmartImageProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'onLoad' | 'onError' | 'src'> {
    // Image URL to load. Falsy values (missing upload, no document yet, etc.) render the fallback directly.
    src?: string | null;
    // Custom placeholder shown when `src` is empty or the image fails to load. Defaults to a muted "no image" icon.
    fallback?: React.ReactNode;
    // Shows a spinner overlay while the image loads — use for large single-image previews/viewers, not grids of thumbnails.
    showSpinner?: boolean;
    // Classes for the wrapping div; only used when `showSpinner` is true.
    containerClassName?: string;
}

// Drop-in replacement for `<img src={url} />` that renders a consistent fallback when the URL
// is empty or fails to load, instead of each screen re-implementing its own broken-image handling.
const SmartImage: React.FC<SmartImageProps> = ({
    src,
    alt = '',
    className = '',
    containerClassName,
    fallback,
    showSpinner = false,
    referrerPolicy = 'origin-when-cross-origin',
    ...rest
}) => {
    const [failed, setFailed] = useState(false);
    const [loading, setLoading] = useState(!!src);

    useEffect(() => {
        setFailed(false);
        setLoading(!!src);
    }, [src]);

    if (!src || failed) {
        return (
            <>
                {fallback ?? (
                    <div className={className || 'w-full h-full flex items-center justify-center text-slate-400 bg-slate-50'}>
                        <ImageOff size={16} className="opacity-60" />
                    </div>
                )}
            </>
        );
    }

    const img = (
        <img
            src={src}
            alt={alt}
            referrerPolicy={referrerPolicy}
            className={`${className}${showSpinner ? ` transition-opacity duration-300 ${loading ? 'opacity-0' : 'opacity-100'}` : ''}`}
            onLoad={() => setLoading(false)}
            onError={() => { setFailed(true); setLoading(false); }}
            {...rest}
        />
    );

    if (!showSpinner) return img;

    return (
        <div className={`relative ${containerClassName || ''}`}>
            {loading && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 z-10 backdrop-blur-sm">
                    <div className="w-8 h-8 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mb-2" />
                </div>
            )}
            {img}
        </div>
    );
};

export default SmartImage;
